/**
 * Where a database view shows a row, or one cell of it, so a crewmate's
 * pointer can stand on what its change touched instead of on the database
 * block. Each view marks its parts (blocksuite/affine/data-view view presets):
 * table cells carry `data-row-id` and `data-column-id` (table/pc-virtual/row/
 * cell.ts, table/pc rows `data-row-id`), kanban cards `data-card-id`
 * (kanban/pc/group.ts), calendar events `data-calendar-event-key`
 * (calendar/pc/grids/calendar-grid-renderer.ts) and gantt bars and milestones
 * `data-bar-row-id` (gantt/pc/bars/bar-renderer.ts). Only the view on screen
 * renders, so the first shown match is the place. A change of the view
 * itself names the header control it set (`data-view-control` on the filter,
 * sort and view options buttons of widget-presets/tools/presets).
 */
export type AgentDatabaseTarget =
  | { rowId: string; columnId?: string; viewControl?: undefined }
  | { viewControl: 'filter' | 'sort' | 'group'; rowId?: undefined };

function selectorsOf(target: AgentDatabaseTarget): string[] {
  if (target.viewControl)
    return [`[data-view-control="${target.viewControl}"]`];
  const { rowId, columnId } = target;
  const row = CSS.escape(rowId);
  const cell = columnId
    ? [`[data-row-id="${row}"][data-column-id="${CSS.escape(columnId)}"]`]
    : [];
  return [
    ...cell,
    `[data-calendar-event-key="${row}"]`,
    `[data-card-id="${row}"]`,
    `[data-bar-row-id="${row}"]`,
    // A row without its cell: the row's first cell, or the row itself.
    `[data-row-id="${row}"][data-column-id]`,
    `[data-row-id="${row}"]`,
  ];
}

function shown(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** The element showing the row (or its cell) inside `root`, or null when the view does not show it. */
export function agentDatabaseTargetElement(
  root: ParentNode,
  target: AgentDatabaseTarget
): Element | null {
  for (const selector of selectorsOf(target)) {
    for (const element of root.querySelectorAll(selector)) {
      if (shown(element)) return element;
    }
  }
  return null;
}

/** What a crewmate's pointer target names inside a database block. */
export interface AgentPointerViewTarget {
  rowId?: string;
  columnId?: string;
  viewControl?: 'filter' | 'sort' | 'group';
}

/**
 * Where a database block's open view shows a pointer target: the header
 * control a view change set, else the row or its cell. Undefined when the
 * target names neither, or a control the view does not show (e.g. read
 * only): the pointer stands at the block. Null when the view does not show
 * the row.
 */
export function agentPointerViewElement(
  block: ParentNode,
  target: AgentPointerViewTarget
): Element | null | undefined {
  if (target.viewControl) {
    const control = agentDatabaseTargetElement(block, {
      viewControl: target.viewControl,
    });
    if (control) return control;
  }
  if (!target.rowId) return undefined;
  return agentDatabaseTargetElement(block, {
    rowId: target.rowId,
    columnId: target.columnId,
  });
}
